import { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Typography,
  TextField,
  Modal,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';

// Context
import { AuthContext } from '../../context/AuthProvider';

// Services
import proposalsServices from '../../services/proposalsServices';
import smsServices from '../../services/smsServices';

// Utils
import { calculateTotalPay } from '../../utils/proposalCalculations';

// Styles
import { button } from '../../static/theme-styles';

const modal_style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 600, md: 700 },
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflow: 'auto',
  backgroundColor: '#fff',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  borderRadius: 2,
};

// Default email templates
const getDefaultAmountDueMessage = (clientName, publicId, balance) => 
`Dear ${clientName},

This is a friendly reminder regarding your outstanding balance for Proposal ${publicId}.

Your current balance due is $${balance.toLocaleString()}.

Please click the link below to view your proposal and make a payment at your earliest convenience.

If you have already made a payment, please disregard this notice.

Thank you for your business!

Best regards,
Airmaxx Team`;

const getDefaultReceiptMessage = (clientName, receiptId) =>
`Dear ${clientName},

Thank you for your payment!

Your payment has been received and processed successfully. Please find your payment receipt attached (Receipt #${receiptId}).

Click the link below to view and download your receipt.

If you have any questions, please don't hesitate to contact us.

Thank you for choosing Airmaxx!

Best regards,
Airmaxx Team`;

/**
 * SendPaymentEmailModal - Preview modal for sending Amount Due or Payment Receipt emails
 */
const SendPaymentEmailModal = ({ isOpen, onClose, proposal, type, onSuccess }) => {
  const { userData } = useContext(AuthContext);
  const [sending, setSending] = useState(false);
  const [clientEmail, setClientEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');

  const isAmountDue = type === 'amountDue';
  const title = isAmountDue ? 'Send Payment Reminder' : 'Send Payment Receipt';
  const icon = isAmountDue ? <AttachMoneyIcon /> : <ReceiptIcon />;
  const sentCount = isAmountDue 
    ? (proposal?.amountDueRemindersSent || 0) 
    : (proposal?.paymentReceiptsSent || 0);
  const lastSentDate = isAmountDue 
    ? proposal?.lastAmountDueSentAt 
    : null; // TODO: add lastPaymentReceiptSentAt if needed

  // All clients linked to the property
  const propertyClients = (proposal?.property?.clients || [])
    .map(cp => cp.client)
    .filter(Boolean);

  // Find selected client: prioritize proposal.selectedClientId, then fall back to first
  const findDefaultClient = () => {
    if (proposal?.selectedClientId && propertyClients.length > 0) {
      const match = propertyClients.find(c => c.id === proposal.selectedClientId);
      if (match) return match;
    }
    return propertyClients[0] || null;
  };

  const client = propertyClients.find(c => c.id === selectedClientId) || findDefaultClient();
  const clientName = client?.fullName || 'Customer';
  const propertyAddress = proposal?.property?.place?.label || 
    `${proposal?.property?.streetNumber || ''} ${proposal?.property?.streetName || ''}, ${proposal?.property?.city || ''}`.trim();

  // Calculate totals using the proper calculation function
  const totalPay = calculateTotalPay(proposal);
  const paidAmount = proposal?.payments?.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) || 0;
  const remainingBalance = totalPay - paidAmount;
  const receiptId = proposal?.receiptId || `RCPT-${proposal?.publicId}`;

  // Initialize selected client when modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultClient = findDefaultClient();
      setSelectedClientId(defaultClient?.id || '');
    }
  }, [isOpen, proposal?.selectedClientId]);

  // Update email and message when selected client changes
  useEffect(() => {
    if (client?.email) {
      setClientEmail(client.email);
      setEmailError('');
    } else {
      setClientEmail('');
      setEmailError('Client email not found');
    }

    if (isAmountDue) {
      setEmailMessage(getDefaultAmountDueMessage(clientName, proposal?.publicId, remainingBalance));
    } else {
      setEmailMessage(getDefaultReceiptMessage(clientName, receiptId));
    }
  }, [selectedClientId, client, isOpen, isAmountDue, clientName, proposal?.publicId, remainingBalance, receiptId]);

  const handleClientChange = (e) => {
    setSelectedClientId(e.target.value);
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setClientEmail(email);
    if (!email) {
      setEmailError('Email is required');
    } else if (!validateEmail(email)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError('');
    }
  };

  const handleSend = async () => {
    if (!clientEmail || emailError) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSending(true);
    try {
      if (isAmountDue) {
        await proposalsServices.sendAmountDue(proposal.id, { message: emailMessage, email: clientEmail });
        toast.success('Payment reminder sent successfully!');
      } else {
        await proposalsServices.sendPaymentReceipt(proposal.id, { message: emailMessage, email: clientEmail });
        toast.success('Payment receipt sent successfully!');
      }
      onSuccess && onSuccess();
      onClose();

      // Fire-and-forget: send SMS notification to client
      try {
        if (client?.phoneNumber) {
          const smsData = {
            proposalId: proposal.id,
            clientPhone: client.phoneNumber,
            clientName: client.firstName || client.fullName?.split(' ')[0] || '',
          };

          const smsResponse = isAmountDue
            ? await smsServices.sendAmountDueSms({ ...smsData, amountDue: remainingBalance })
            : await smsServices.sendReceiptSms(smsData);

          if (smsResponse.data?.success) {
            toast.info('SMS notification also sent to client');
          }
        }
      } catch (smsError) {
        console.warn('SMS notification failed (email was sent successfully):', smsError);
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error(`Failed to send ${isAmountDue ? 'payment reminder' : 'receipt'}`);
    } finally {
      setSending(false);
    }
  };

  const handleResetMessage = () => {
    if (isAmountDue) {
      setEmailMessage(getDefaultAmountDueMessage(clientName, proposal?.publicId, remainingBalance));
    } else {
      setEmailMessage(getDefaultReceiptMessage(clientName, receiptId));
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={modal_style}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            bgcolor: isAmountDue ? '#ff9800' : '#4caf50', 
            color: '#fff', 
            p: 1, 
            borderRadius: 1, 
            mr: 2,
            display: 'flex',
            alignItems: 'center'
          }}>
            {icon}
          </Box>
          <Box>
            <Typography variant="h6">{title}</Typography>
            {sentCount > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    bgcolor: '#ff9800', 
                    color: '#fff', 
                    px: 1, 
                    py: 0.25, 
                    borderRadius: 1,
                    fontWeight: 500 
                  }}
                >
                  Previously sent: {sentCount} time{sentCount > 1 ? 's' : ''}
                </Typography>
                {lastSentDate && (
                  <Typography variant="caption" color="warning.main">
                    Last notification {new Date(lastSentDate).toLocaleDateString('en-US')}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Proposal Info */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 45%', minWidth: 150 }}>
              <Typography variant="caption" color="text.secondary">Proposal</Typography>
              <Typography variant="body2" fontWeight="bold">{proposal?.publicId}</Typography>
            </Box>
            <Box sx={{ flex: '1 1 45%', minWidth: 150 }}>
              <Typography variant="caption" color="text.secondary">Customer</Typography>
              {propertyClients.length > 1 ? (
                <FormControl fullWidth size="small" sx={{ mt: 0.5 }}>
                  <Select
                    value={selectedClientId}
                    onChange={handleClientChange}
                    displayEmpty
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {propertyClients.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.fullName}{c.id === proposal?.selectedClientId ? ' (proposal client)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Typography variant="body2">{clientName}</Typography>
              )}
            </Box>
            <Box sx={{ flex: '1 1 100%' }}>
              <Typography variant="caption" color="text.secondary">Property</Typography>
              <Typography variant="body2">{propertyAddress}</Typography>
            </Box>
          </Box>

          {isAmountDue && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Total: ${totalPay.toLocaleString()}</Typography>
                  {paidAmount > 0 && (
                    <Typography variant="body2" color="success.main">Paid: -${paidAmount.toLocaleString()}</Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                  <Typography variant="h5" fontWeight="bold" color="error.main">
                    ${remainingBalance.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </>
          )}

          {!isAmountDue && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Receipt ID</Typography>
                <Typography variant="body1" fontWeight="bold">{receiptId}</Typography>
              </Box>
            </>
          )}
        </Box>

        {/* Email Input */}
        <TextField
          fullWidth
          label="Send to Email"
          value={clientEmail}
          onChange={handleEmailChange}
          error={!!emailError}
          helperText={emailError}
          size="small"
          sx={{ mb: 2 }}
        />

        {/* Editable Email Message */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Email Message
            </Typography>
            <Button size="small" onClick={handleResetMessage} sx={{ textTransform: 'none' }}>
              Reset to Default
            </Button>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            variant="outlined"
            sx={{ 
              '& .MuiOutlinedInput-root': {
                fontFamily: 'monospace',
                fontSize: '0.85rem',
              }
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            This message will be included in the email. A link to the proposal will be added automatically.
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending || !!emailError || !clientEmail}
            sx={{ 
              ...button('primary', 'secondary'), 
              flex: 1,
              bgcolor: isAmountDue ? '#ff9800' : '#4caf50',
              '&:hover': {
                bgcolor: isAmountDue ? '#f57c00' : '#388e3c',
              }
            }}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : icon}
          >
            {sending ? 'Sending...' : `Send ${isAmountDue ? 'Reminder' : 'Receipt'}`}
          </Button>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={sending}
            sx={{ flex: 1 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SendPaymentEmailModal;
